import { createFileRoute } from "@tanstack/react-router";
import { SiteShell } from "@/components/site/SiteShell";
import { PillarArticle, articleJsonLdForPillar } from "@/components/site/PillarArticle";
import { InlineAuditSection } from "@/components/audit/InlineAuditSection";
import { buildHead, breadcrumbJsonLd, faqJsonLd } from "@/lib/seo";
import cover from "@/assets/cover-pillar-performance.jpg";
import cover640 from "@/assets/cover-pillar-performance-640.webp";
import cover960 from "@/assets/cover-pillar-performance-960.webp";
import cover1280 from "@/assets/cover-pillar-performance-1280.webp";
import { setEdgeHtmlCache } from "@/lib/cache-headers";

const COVER_SRCSET = `${cover640} 640w, ${cover960} 960w, ${cover1280} 1280w`;
const COVER_SIZES = "(min-width: 768px) 720px, calc(100vw - 48px)";

const PATH = "/resources/shopify-performance-optimization-guide";
const ALT = "/es/recursos/guia-optimizacion-rendimiento-shopify";
const TITLE = "Shopify Performance Optimization Guide: LCP, INP & Revenue · Shugert";
const DESC =
  "The full Shopify performance playbook for $1M+ DTC brands: how we diagnose, fix and ship LCP, INP and CLS work that actually compounds into revenue.";
const PUBLISHED = "2026-05-19T09:00:00.000Z";
const MODIFIED = "2026-05-19T09:00:00.000Z";

const SECTIONS = [
  {
    id: "performance-is-revenue",
    heading: "Shopify performance is a revenue problem, not a tech problem",
    blocks: [
      "Most Shopify performance reports get filed under \"engineering nice-to-have.\" That framing is why your store is slow. On every store we've audited in the $1M–$50M range, a 1-second improvement in mobile LCP correlates with a 6–12% lift in conversion rate. On Black Friday traffic, those points compound into six figures inside a single week.",
      "Speed is the first impression a paid-traffic visitor has of your brand. If your hero takes 4.2 seconds to render and your CTA is jank-frozen for another 600ms, you've burned half of a Meta click before the user has decided whether they care. Performance work isn't \"polish.\" It's the cheapest CRO lever you own.",
      "This guide walks through the entire playbook we use on Shopify Plus stores: the metrics that matter, how we diagnose, the fixes we ship in order of leverage, and how to keep speed from regressing the moment marketing adds a new app.",
    ],
  },
  {
    id: "metrics-that-matter",
    heading: "The metrics that matter: LCP, INP, CLS",
    blocks: [
      "Forget lab scores. Google ranks and your customers experience field metrics, what real Chrome users see, aggregated in the Chrome User Experience Report (CrUX). The three that move the needle on Shopify are LCP, INP, and CLS.",
      "### LCP (Largest Contentful Paint)",
      "Time until the largest visible element renders. On Shopify storefronts this is almost always your hero image or PDP product photo. Target: under 2.5s on mobile P75. Stores under 1.8s consistently outperform peers in checkout funnel completion.",
      "### INP (Interaction to Next Paint)",
      "INP replaced FID in March 2024 and most Shopify themes regressed silently. It measures how long the slowest interaction takes to paint a response. Heavy theme JavaScript, third-party tag managers, and review widgets push INP into the 400–800ms danger zone. Target: under 200ms.",
      "### CLS (Cumulative Layout Shift)",
      "Visual jank. Ads, late-loading fonts, missing image dimensions and sticky banners all contribute. Shopify themes that don't reserve space for app-injected content are the most common offenders. Target: under 0.1.",
    ],
  },
  {
    id: "diagnose-before-optimize",
    heading: "Diagnose before you optimize: how we audit a Shopify store",
    blocks: [
      "Every performance project we run starts with the same 4-hour diagnostic. Skip it and you'll spend a week optimizing the wrong asset.",
      "- Pull 28-day CrUX field data for the home, top 5 collections and top 10 PDPs. Real users only, no Lighthouse fantasies.",
      "- Run WebPageTest against a throttled mobile profile from the buyer's actual region. We default to a Moto G Power on Fast 3G.",
      "- Inventory every script tag with `shopify.theme.scripts` and `window.performance.getEntriesByType('resource')`. Group by vendor.",
      "- Map every render-blocking request in the critical path of LCP. This is where you'll find your wins.",
      "- Audit `theme.liquid`, `cart-drawer.js`, and any custom section that ships before First Contentful Paint.",
      "The output is a priority-ordered list. Most stores have 4–7 high-leverage fixes and a long tail of marginal wins. Ship the top three first.",
    ],
  },
  {
    id: "image-stack",
    heading: "The image stack: your biggest LCP lever",
    blocks: [
      "On 80% of Shopify stores we audit, the LCP element is an image that's 4× larger than it needs to be, served as JPEG, no `fetchpriority`, and not preloaded. Fix the image stack and you've solved most of your LCP problem before touching JavaScript.",
      "### The non-negotiables",
      "- Serve WebP or AVIF via Shopify's image_url filter with explicit width parameters.",
      "- Set `width` and `height` attributes on every <img>, this kills CLS in one line.",
      "- Use `loading=\"eager\" fetchpriority=\"high\"` on the LCP image only.",
      "- Preload the LCP image in `<head>` with the responsive `imagesrcset` attribute.",
      "- Add `loading=\"lazy\"` to every below-the-fold image, but never to the LCP candidate.",
      "### Responsive srcset that actually helps",
      "Shipping a 2400px hero to a 390px iPhone wastes 70% of the bandwidth budget. We generate srcsets across 6 widths (400/600/800/1200/1600/2400) with `sizes` that match your actual breakpoints. A correctly tuned hero usually drops 800–1400ms off mobile LCP.",
    ],
  },
  {
    id: "app-bloat",
    heading: "App bloat and theme third parties",
    blocks: [
      "The average Shopify Plus store ships 14 apps. Most inject blocking JavaScript on every page, including pages that don't use them. A review widget that runs on /collections/* despite only being used on PDPs is single-handedly costing you 200ms of INP.",
      "### The audit",
      "Open Chrome DevTools → Coverage tab → reload. Sort by unused bytes. Every row over 50KB unused is a candidate for lazy-loading or removal. We typically find that 30–50% of third-party JS shipped to the user is never executed.",
      "### Fixes that ship the same day",
      "- Move analytics tags (GA4, Meta Pixel, TikTok) into Shopify's web pixel sandbox, they're already async there.",
      "- Lazy-load review widgets via IntersectionObserver, not on DOMContentLoaded.",
      "- Replace heavyweight cart drawers (Slide Cart, Rebuy variants) with native Shopify Ajax cart calls.",
      "- Audit any app that injects a `<script>` into `theme.liquid`, most can be moved to specific templates.",
    ],
  },
  {
    id: "javascript-discipline",
    heading: "JavaScript: deferred, scoped, and budgeted",
    blocks: [
      "INP is mostly a JavaScript problem. Long tasks block the main thread and your tap targets feel frozen. The fix is discipline: every script either earns its place in the critical path or it gets deferred.",
      "### The JS budget",
      "We enforce a 170KB compressed budget for first-party JS in the critical path on every store we run. That's enough for a real theme. Anything beyond that needs justification.",
      "### Patterns we ship",
      "- Use native browser features (dialog, details, scroll-snap) instead of JS-driven equivalents.",
      "- Defer non-critical scripts with `type=\"module\"` or `defer`, never inline blocking <script> in <head>.",
      "- Break apart long tasks with `scheduler.yield()` where supported, or `setTimeout(..., 0)` as a fallback.",
      "- Cache parsed JSON in IndexedDB for predictive carting on PLP→PDP transitions.",
    ],
  },
  {
    id: "shopify-plus-levers",
    heading: "Server-rendered checkout and Shopify Plus levers",
    blocks: [
      "Shopify Plus opens the door to performance plays that aren't available on standard plans. If you're on Plus and not using these, you're leaving speed on the table.",
      "### Checkout extensibility",
      "Move all checkout customizations from script tag injections into checkout extensions. The new server-rendered checkout is 30–40% faster than the legacy `checkout.liquid` for most stores. It also unlocks Shopify Functions for discounts, payment methods, and delivery customizations without round-tripping a third party.",
      "### Hydrogen / Oxygen",
      "Hydrogen is React-on-the-edge for storefronts that have outgrown Liquid. We deploy headless when a brand needs hero personalization, complex PDPs, or to share a single backend across multiple frontends. Hydrogen isn't faster by default, it's faster when you commit to streaming and partial hydration.",
      "### Markets",
      "Use Shopify Markets to serve regional storefronts from the closest edge. The CDN routing alone shaves 200–400ms from international LCP.",
    ],
  },
  {
    id: "ship-and-measure",
    heading: "How to ship and measure performance work safely",
    blocks: [
      "Performance regresses the moment you stop watching. Most stores we inherit had a fast moment 18 months ago and then a marketing team added 4 apps. The fix is observability, not heroics.",
      "### Continuous measurement",
      "- Wire SpeedCurve or Calibre to your most important templates. Set alerts on P75 LCP and INP.",
      "- Use Real User Monitoring via the `web-vitals` library, pipe to your analytics warehouse. Segment by device, geo and template.",
      "- Make a Slack alert fire when LCP P75 crosses your budget for 24 hours.",
      "### Release process",
      "- Every new app gets benchmarked on a private duplicate theme before going live.",
      "- Every theme deploy ships through a preview URL with WebPageTest comparing against main.",
      "- Quarterly performance review with marketing, kill the apps that aren't earning their JS budget.",
      "Done well, this becomes part of your store's metabolism. Done badly, you'll be re-running this exact playbook again in 18 months.",
    ],
  },
];

const FAQS = [
  {
    q: "How long does a Shopify performance project take?",
    a: "Most engagements ship in 2–4 weeks. The first week is diagnostic and benchmarking; weeks 2–3 are implementation of the highest-leverage fixes; week 4 is regression testing and handover.",
  },
  {
    q: "Will optimizing performance break my apps?",
    a: "No, we deploy through a development theme and full QA the cart, checkout, search, reviews, and any integrations before we touch the live theme. Cutovers happen in low-traffic windows with rollback ready.",
  },
  {
    q: "What LCP target should I aim for?",
    a: "Under 2.5s on mobile P75 is the baseline Google rewards. Best-in-class Shopify Plus stores ship sub-1.8s and see compounding conversion gains. If you're above 3.5s today, plan to get under 2.5s before chasing 1.8s.",
  },
  {
    q: "Do we need to leave Liquid for performance?",
    a: "Not unless you have other reasons to go headless (multi-storefront, complex personalization, large editorial layer). A well-built Liquid theme with disciplined JS and a tuned image stack matches Hydrogen on most KPIs and costs a fraction to maintain.",
  },
  {
    q: "How do we keep performance from regressing?",
    a: "Wire real-user monitoring (SpeedCurve or web-vitals → your analytics warehouse), alert when P75 LCP or INP cross your budget, and gate every new app through a performance benchmark before it ships.",
  },
];

const RELATED = [
  { label: "Read all performance guides →", href: "/resources/performance" },
  { label: "Service: Shopify Performance & Core Web Vitals", href: "/shopify-performance" },
  { label: "Pillar: Shopify Plus Agency", href: "/shopify-plus-agency" },
];

export const Route = createFileRoute("/resources/shopify-performance-optimization-guide")({
  loader: async () => { await setEdgeHtmlCache(); },
  head: () =>
    buildHead({
      locale: "en",
      title: TITLE,
      description: DESC,
      path: PATH,
      altPath: ALT,
      ogImage: cover,
      type: "article",
      author: "Samuel Noriega",
      articleAuthor: "Samuel Noriega",
      articleSection: "Performance",
      publishedTime: PUBLISHED,
      modifiedTime: MODIFIED,
      jsonLd: [
        articleJsonLdForPillar({ image: cover,
          title: TITLE,
          description: DESC,
          path: PATH,
          publishedTime: PUBLISHED,
          modifiedTime: MODIFIED,
          section: "Performance",
        }),
        breadcrumbJsonLd([
          { name: "Home", path: "/" },
          { name: "Resources", path: "/resources" },
          { name: "Performance", path: "/resources/performance" },
          { name: "Performance Optimization Guide", path: PATH },
        ]),
        faqJsonLd(FAQS),
      ],
    }),
  component: () => (
    <SiteShell>
      <PillarArticle
        locale="en"
        path={PATH}
        eyebrow="Performance"
        title="The Shopify Performance Optimization Guide for $1M+ DTC brands"
        intro="Everything we ship to take a Shopify or Shopify Plus store from a 3.8s mobile LCP to sub-1.8s, without breaking the cart, the checkout or the marketing team's apps."
        updated="May 2026"
        readingMinutes={18}
        hubHref="/resources/performance"
        hubLabel="Browse all performance guides"
        sections={SECTIONS}
        cover={{ src: cover, srcSet: COVER_SRCSET, sizes: COVER_SIZES, alt: "The Shopify Performance Optimization Guide, illustrated zebra cover" }}
        midCTA={{
          eyebrow: "Where we usually start",
          title: "Get a Pulse diagnostic on your store's performance",
          body: "A senior engineer reviews your LCP, INP and CLS field data, audits your theme and apps, and sends you the priority-ordered fix list, usually within 5 business days.",
          href: "/pulse",
          label: "See Pulse",
        }}
        finalCTA={{
          eyebrow: "Ready to fix it",
          title: "Get a scoped performance project in 48 hours",
          body: "Tell us your store, your current LCP, and what you've tried. We'll send a fixed-scope plan with timelines, deliverables and price, no long sales process.",
          href: "/get-a-quote?source=pillar-performance",
          label: "Request a scope",
        }}
        faqs={FAQS}
        related={RELATED}
        publishedTime={PUBLISHED}
        modifiedTime={MODIFIED}
      />
      <InlineAuditSection locale="en" />
    </SiteShell>
  ),
});